import React, { useState, useEffect } from 'react';
import './RidesPage.css';
import { apiRequest, parseResponse, getLoggedInUserId, unwrapRideGroups, bookingIdOf } from './utils/api';
import {
  customerRideSections,
  mergeRideGroups,
  ridePhaseOf,
  rideBadge,
  requestedVehicle,
  hasAssignedVendor,
  hasAssignedCar,
  hasAssignedDriver,
  needsOnlinePayNow,
  needsCompletedCashPay,
  RIDE_PHASE
} from './utils/rides';
import { useLanguage } from './i18n/LanguageContext';

const RidesPage = ({ onRideSelect, onBack, onNavigateTab, onPayNow, split = false, selectedRideId = null }) => {
  const { t } = useLanguage();
  const [rideGroups, setRideGroups] = useState({
    created: [],
    vendorAssigned: [],
    carAssigned: [],
    confirmed: [],
    cancelled: [],
    completed: []
  });
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRides();
  }, []);

  const fetchRides = async () => {
    try {
      setLoading(true);
      setError(null);

      const userId = getLoggedInUserId();
      if (!userId) {
        throw new Error('User ID not found. Please complete guest verification first.');
      }

      const response = await apiRequest(`/bookings/customer/${userId}`, {
        method: 'GET'
      });
      const data = await parseResponse(response);

      if (!response.ok || data?.status === false) {
        throw new Error(data?.message || 'Failed to fetch rides');
      }

      setRideGroups(unwrapRideGroups(data));
    } catch (err) {
      console.error('Error fetching rides:', err);
      setError(err.message || 'Failed to load rides. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const rideDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      let dateLabel = '';
      if (rideDate.getTime() === today.getTime()) {
        dateLabel = 'TODAY';
      } else if (rideDate.getTime() === yesterday.getTime()) {
        dateLabel = 'YESTERDAY';
      } else {
        dateLabel = date.toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short'
        }).toUpperCase();
      }

      const time = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      return `${dateLabel}, ${time}`;
    } catch (e) {
      return dateString;
    }
  };

  const assignmentLine = (ride) => {
    const phase = ridePhaseOf(ride);
    if (phase === RIDE_PHASE.WAITING_PAYMENT || phase === RIDE_PHASE.CANCELLED) {
      return '';
    }
    if (!hasAssignedVendor(ride) && phase !== RIDE_PHASE.COMPLETED) {
      return t('Waiting for vendor');
    }
    if (!hasAssignedCar(ride) && phase !== RIDE_PHASE.COMPLETED) {
      const vendor = ride.vendorName || t('Vendor assigned');
      return `${vendor} • ${t('Waiting for car')}`;
    }
    if (hasAssignedCar(ride)) {
      const parts = [ride.carName, ride.carNumber].filter(Boolean);
      if (hasAssignedDriver(ride) && ride.driverName) {
        parts.push(ride.driverName);
      }
      return parts.join(' • ') || t('Car assigned');
    }
    return '';
  };

  const sections = customerRideSections(rideGroups);
  const filteredRides = selectedStatus === 'ALL'
    ? mergeRideGroups(rideGroups)
    : (sections[selectedStatus] || []);

  const emptyLabel = {
    ALL: t('Your ride history will appear here'),
    WAITING: t('No rides waiting for payment'),
    UPCOMING: t('No upcoming rides'),
    PAST: t('No past rides'),
    CANCELLED: t('No cancelled rides')
  }[selectedStatus];

  return (
    <div className={`rides-page${split ? ' is-split' : ''}`}>
      <div className="rides-header">
        <button className="back-button rides-back" onClick={onBack} aria-label="Go back">
          <img src={process.env.PUBLIC_URL + '/images/back_button.svg'} alt="" />
        </button>
        <div className="rides-header-text">
          <h1 className="rides-title">{t('Rides')}</h1>
          <p className="rides-subtitle">{t('Review your journey so far.')}</p>
        </div>
      </div>

      <div className="status-filters">
        {[
          ['ALL', 'All'],
          ['WAITING', 'Waiting for payment'],
          ['UPCOMING', 'Upcoming'],
          ['PAST', 'Past rides'],
          ['CANCELLED', 'Cancelled']
        ].map(([id, label]) => (
          <button
            key={id}
            className={`status-filter-btn ${selectedStatus === id ? 'active' : ''}`}
            onClick={() => setSelectedStatus(id)}
          >
            {t(label)}
          </button>
        ))}
      </div>

      <div className="rides-content">
        {loading && (
          <div className="rides-loading">
            <div className="loading-spinner"></div>
            <p>{t('Loading rides...')}</p>
          </div>
        )}

        {error && (
          <div className="rides-error">
            <p>{error}</p>
            <button className="retry-button" onClick={fetchRides}>
              {t('Retry')}
            </button>
          </div>
        )}

        {!loading && !error && filteredRides.length === 0 && (
          <div className="rides-empty">
            <p>{t('No rides found')}</p>
            <p className="empty-subtitle">{emptyLabel}</p>
          </div>
        )}

        {!loading && !error && filteredRides.length > 0 && (
          <div className="rides-list card-view">
            {filteredRides.map((ride, index) => {
              const phase = ridePhaseOf(ride);
              const badge = rideBadge(ride);
              const fare = ride.fare ? parseFloat(ride.fare) : 0;
              const rideId = bookingIdOf(ride);
              const isSelected = selectedRideId != null && String(rideId) === String(selectedRideId);
              const statusClass = String(phase || badge).toLowerCase().replace(/\s+/g, '-');
              const requested = requestedVehicle(ride);
              const showPay = needsOnlinePayNow(ride) || needsCompletedCashPay(ride);
              const extra = assignmentLine(ride);

              return (
                <button
                  type="button"
                  key={rideId || index}
                  className={`ride-card-view${isSelected ? ' is-selected' : ''}`}
                  onClick={() => onRideSelect && onRideSelect(ride)}
                >
                  <span className={`ride-status-badge ${statusClass}`}>{t(badge)}</span>
                  <div className="ride-card-main">
                    <span className="ride-date-time">
                      {formatDate(ride.carRequestTime || ride.createdAt)}
                    </span>
                    {requested ? (
                      <span className="ride-vehicle-type">{t('Requested: {name}', { name: requested })}</span>
                    ) : null}
                    {extra ? <span className="ride-assignment-line">{extra}</span> : null}
                    <div className="ride-card-locations">
                      <div className="ride-location-row">
                        <span className="location-dot-filled"></span>
                        <span className="ride-location-text">{ride.startLocation || 'Pickup Location'}</span>
                      </div>
                      <div className="ride-location-row">
                        <span className="location-dot-outline"></span>
                        <span className="ride-location-text">{ride.endLocation || 'Drop Location'}</span>
                      </div>
                    </div>
                    {showPay && onPayNow && (
                      <span
                        className="ride-pay-now"
                        onClick={(event) => {
                          event.stopPropagation();
                          onPayNow(ride);
                        }}
                      >
                        {t('Pay now')}
                      </span>
                    )}
                  </div>
                  <div className="ride-card-fare">
                    <span className="ride-fare-label">{t('Total fare')}</span>
                    <span className="ride-fare-amount">₹ {fare.toLocaleString('en-IN')}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <nav className="bottom-navigation rides-bottom-nav">
        <button type="button" className="nav-item" onClick={() => (onNavigateTab ? onNavigateTab('HOME') : onBack && onBack())}>
          <svg width="27" height="24" viewBox="0 0 27 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24.6172 11.5303H23.5068C22.6785 11.5304 22.0068 12.2019 22.0068 13.0303V22.8242H17.0068V15.8535C17.0068 15.0251 16.3352 14.3535 15.5068 14.3535H10.9072C10.0788 14.3535 9.40729 15.0251 9.40723 15.8535V22.8242H4.40723V13.0303C4.40723 12.2019 3.73556 11.5304 2.90723 11.5303H1.79688L13.207 1.1748L24.6172 11.5303Z" stroke="currentColor" strokeWidth="2" />
          </svg>
          <span>{t('Home')}</span>
        </button>
        <button type="button" className="nav-item active">
          <svg width="32" height="17" viewBox="0 0 32 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M26.5 15.9995H29.5C30.4 15.9995 31 15.3995 31 14.4996V9.99971C31 8.64975 29.95 7.44979 28.75 7.1498C26.05 6.39982 22 5.49985 22 5.49985C22 5.49985 20.05 3.39992 18.7 2.04997C17.95 1.44999 17.05 1 16 1H5.5C4.6 1 3.85 1.59998 3.4 2.34996L1.3 6.69981C1.10137 7.27913 1 7.88733 1 8.49976V14.4996C1 15.3995 1.6 15.9995 2.5 15.9995H5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{t('Ride')}</span>
        </button>
        <button type="button" className="nav-item" onClick={() => onNavigateTab && onNavigateTab('HELP')}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{t('Contact')}</span>
        </button>
        <button type="button" className="nav-item" onClick={() => onNavigateTab && onNavigateTab('PROFILE')}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
          </svg>
          <span>{t('Profile')}</span>
        </button>
      </nav>
    </div>
  );
};

export default RidesPage;
